import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  X
} from "lucide-react";
import { API } from "@/utils/api";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const DAYS_SHORT = ["S", "M", "T", "W", "T", "F", "S"];

export default function MobileDatePickerModal({
  isOpen,
  onClose,
  selectedDate,
  onSelectDate,
  title = "Select Date"
}) {
  const [currentYear, setCurrentYear] = useState(() => {
    return selectedDate ? new Date(selectedDate).getFullYear() : new Date().getFullYear();
  });
  const [currentMonth, setCurrentMonth] = useState(() => {
    return selectedDate ? new Date(selectedDate).getMonth() + 1 : new Date().getMonth() + 1;
  });

  const [tempDate, setTempDate] = useState(selectedDate || new Date().toISOString().split("T")[0]);
  const [monthData, setMonthData] = useState({});

  useEffect(() => {
    if (isOpen) {
      const initDate = selectedDate || new Date().toISOString().split("T")[0];
      setTempDate(initDate);
      const d = new Date(initDate);
      if (!isNaN(d.getTime())) {
        setCurrentYear(d.getFullYear());
        setCurrentMonth(d.getMonth() + 1);
      }
    }
  }, [isOpen, selectedDate]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchMonth = async () => {
      try {
        const res = await axios.get(`${API}/calendar/month-summary`, {
          params: { year: currentYear, month: currentMonth }
        });
        if (res.data?.days) {
          setMonthData(res.data.days);
        }
      } catch (err) {
        console.warn("Could not fetch month summary:", err);
      }
    };
    fetchMonth();
  }, [isOpen, currentYear, currentMonth]);

  if (!isOpen) return null;

  const handlePrevMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  };

  // Build calendar matrix
  const firstDayOfMonth = new Date(currentYear, currentMonth - 1, 1).getDay();
  const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();

  const calendarDays = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const mm = String(currentMonth).padStart(2, "0");
    const dd = String(day).padStart(2, "0");
    calendarDays.push(`${currentYear}-${mm}-${dd}`);
  }

  const handleOk = () => {
    if (onSelectDate) {
      onSelectDate(tempDate);
    }
    onClose();
  };

  // Formatted display date for top banner
  const dateObj = new Date(tempDate + "T00:00:00");
  const formattedDayText = !isNaN(dateObj.getTime())
    ? dateObj.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric"
      })
    : tempDate;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      data-testid="mobile-date-picker-modal"
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-xs" onClick={onClose} />

      <div
        className="relative bg-white w-full max-w-sm rounded-3xl shadow-2xl border border-pink-200 z-10 overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Header: Large Selected Date Display */}
        <div className="bg-gradient-to-r from-pink-950 via-pink-900 to-rose-900 text-white p-5 space-y-1">
          <span className="text-[11px] font-bold uppercase tracking-wider text-pink-200 block">
            {title}
          </span>
          <h2 className="text-2xl font-black tracking-tight" data-testid="picker-display-date">
            {formattedDayText}
          </h2>
        </div>

        {/* Month Navigation Row */}
        <div className="p-4 flex items-center justify-between border-b border-pink-100">
          <span className="text-sm font-black text-pink-950">
            {MONTH_NAMES[currentMonth - 1]} {currentYear}
          </span>

          <div className="flex items-center gap-1">
            <button
              type="button"
              data-testid="picker-prev-month"
              onClick={handlePrevMonth}
              className="p-1.5 rounded-xl hover:bg-pink-100 text-pink-950 transition-colors"
              title="Previous Month"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              data-testid="picker-next-month"
              onClick={handleNextMonth}
              className="p-1.5 rounded-xl hover:bg-pink-100 text-pink-950 transition-colors"
              title="Next Month"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Weekday Labels */}
        <div className="px-4 pt-2">
          <div className="grid grid-cols-7 gap-1 text-center text-xs font-black text-gray-400">
            {DAYS_SHORT.map((d, i) => (
              <div key={i}>{d}</div>
            ))}
          </div>
        </div>

        {/* Calendar Grid of Dates */}
        <div className="p-4">
          <div className="grid grid-cols-7 gap-1.5">
            {calendarDays.map((dStr, idx) => {
              if (!dStr) {
                return <div key={`empty-${idx}`} className="w-10 h-10" />;
              }

              const dayNum = parseInt(dStr.split("-")[2], 10);
              const info = monthData[dStr];
              const dayBooked = Boolean(info?.day_booked);
              const nightBooked = Boolean(info?.night_booked);
              const isSelected = tempDate === dStr;

              // Color styles per mobile app rules
              let dayBg = "bg-emerald-50 text-emerald-950 hover:bg-emerald-100 border border-emerald-300";

              if (dayBooked && nightBooked) {
                // Fully booked -> Red
                dayBg = "bg-rose-100 text-rose-950 hover:bg-rose-200 border border-rose-300";
              } else if (dayBooked || nightBooked) {
                // Partly booked -> Yellow
                dayBg = "bg-amber-100 text-amber-950 hover:bg-amber-200 border border-amber-300";
              }

              if (isSelected) {
                dayBg = "bg-pink-900 text-white font-black ring-2 ring-pink-950 shadow-md scale-105 border-pink-950";
              }

              return (
                <button
                  key={dStr}
                  type="button"
                  data-testid={`date-cell-${dStr}`}
                  onClick={() => setTempDate(dStr)}
                  className={`w-10 h-10 rounded-2xl flex items-center justify-center font-bold text-xs transition-all ${dayBg}`}
                >
                  {dayNum}
                </button>
              );
            })}
          </div>
        </div>

        {/* Bottom Actions: Cancel and OK */}
        <div className="p-4 bg-gray-50 border-t border-pink-100 flex items-center justify-end gap-3">
          <button
            type="button"
            data-testid="picker-cancel-btn"
            onClick={onClose}
            className="px-5 py-2 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="picker-ok-btn"
            onClick={handleOk}
            className="px-6 py-2 rounded-xl bg-pink-950 hover:bg-pink-900 text-white text-xs font-bold shadow-sm transition-all"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}
